import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Eye, FileStack, Plus } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { RowActions } from "@/components/ui/RowActions";
import { ScreenToolbar } from "@/components/ui/ScreenToolbar";

type Brief = {
  ID: number;
  CODE: string | null;
  NAME: string | null;
  CONTENT: string | null;
  REGISTER_NUMBER: string | null;
  EFFECTIVE_DATE: string | null;
  EXPIRED_DATE: string | null;
  CREATED_BY: number | null;
  CREATED_DATE: string | null;
  DOC_COUNT: number;
};

/** Ba ô lọc của lưới "Bộ hồ sơ" bên hệ cũ. */
type Filters = { name: string; code: string; content: string };
const EMPTY: Filters = { name: "", code: "", content: "" };
const toParams = (f: Filters) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")) as Record<string, string>;

async function fetchBriefs(page: number, pageSize: number, f: Filters) {
  const { data } = await api.get("/document/briefs", {
    params: { page, pageSize, ...toParams(f) },
  });
  return (data.data ?? data) as { items: Brief[]; total: number };
}

export function DocumentRecordsPage() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const { data, isLoading } = useQuery({
    queryKey: ["document-briefs", page, pageSize, applied],
    queryFn: () => fetchBriefs(page, pageSize, applied),
  });

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));
  const text = (label: string, key: keyof Filters) => ({
    label,
    control: (
      <input
        className={filterInputClass}
        value={filters[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    ),
  });

  const columns: Column<Brief>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      key: "action",
      header: "Thao tác",
      width: "90px",
      align: "center",
      render: (r) => (
        <RowActions
          actions={[
            { label: "Xem chi tiết", icon: Eye, onClick: () => nav(`/documents/records/${r.ID}`) },
          ]}
        />
      ),
    },
    {
      key: "code",
      header: "Mã hồ sơ",
      width: "220px",
      render: (r) => <span className="font-mono text-xs">{r.CODE ?? "—"}</span>,
    },
    {
      key: "name",
      header: "Tên hồ sơ",
      render: (r) => <span className="font-medium">{r.NAME ?? "—"}</span>,
    },
    { key: "content", header: "Nội dung", width: "260px", render: (r) => r.CONTENT ?? "—" },
    {
      key: "reg",
      header: "Số ĐK",
      width: "200px",
      render: (r) => <span className="font-mono text-xs">{r.REGISTER_NUMBER ?? "—"}</span>,
    },
    {
      key: "eff",
      header: "Hiệu lực",
      width: "130px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.EFFECTIVE_DATE?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "doc",
      header: "Số văn bản",
      width: "110px",
      align: "right",
      render: (r) => (
        <span className="tabular-nums font-medium text-primary-600">{r.DOC_COUNT}</span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Bộ hồ sơ"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} bộ hồ sơ đang theo dõi`}
        breadcrumb="Quản lý Văn bản › Bộ hồ sơ"
        actions={
          <ScreenToolbar
            actions={[
              {
                label: "Thêm mới",
                icon: Plus,
                altKey: "1",
                tone: "primary",
                onClick: () => nav("/documents/records/new"),
              },
            ]}
          />
        }
      />
      <SearchPanel
        fields={[text("Tên hồ sơ", "name"), text("Mã hồ sơ", "code"), text("Nội dung", "content")]}
        onSubmit={() => {
          setApplied(filters);
          setPage(1);
        }}
      />
      <DataTable<Brief>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.ID}
        isLoading={isLoading}
        empty={
          <>
            <FileStack className="inline h-4 w-4 mr-1" /> Chưa có bộ hồ sơ nào
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
